import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Receipt, AlertCircle, ArrowUpRight, CheckCircle2, Clock } from 'lucide-react';
import type { Contribution } from '../../../../shared/types';

export const PaymentHistory: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'contributions'),
      where('memberId', '==', user.uid),
      orderBy('month', 'desc')
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: Contribution[] = [];
        snap.forEach((doc) => list.push(doc.data() as Contribution));
        setContributions(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Offline history load:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user]);

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;

  return (
    <div className="max-w-4xl mx-auto px-4 py-4 sm:py-8 pb-24 sm:pb-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Receipt className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          <span>{t('history.title')}</span>
        </h1>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('history.subtitle')}
        </p>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : contributions.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">
            {t('dashboard.noContributions')}
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-navy-800">
            {contributions.map((c) => (
              <div key={c.contributionId} className="p-4 sm:p-5 hover:bg-gray-50/50 dark:hover:bg-navy-800/40 transition-colors">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-base text-gray-900 dark:text-white">
                        {c.month}
                      </span>
                      <span
                        className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                          c.status === 'approved'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                            : c.status === 'rejected'
                            ? 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'
                            : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300'
                        }`}
                      >
                        {t(`status.${c.status}`)}
                      </span>
                    </div>

                    <div className="text-xs text-gray-400">
                      <span>{t('history.submittedAt')}: {c.submittedInDhakaTime || c.submittedAt}</span>
                      {c.attemptNo > 1 && <span className="ml-2 font-medium">(Attempt #{c.attemptNo})</span>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-4">
                    <div className="text-right">
                      <div className="font-bold text-base text-gray-900 dark:text-white">
                        {fmt(c.totalAmount || c.amount + (c.lateFee || 0))}
                      </div>
                      <div className="text-xs text-gray-400">
                        {fmt(c.amount)} {c.lateFee > 0 ? `+ ${fmt(c.lateFee)} late fee` : ''}
                      </div>
                    </div>

                    {c.status === 'rejected' && (
                      <Link
                        to={`/submit?month=${c.month}`}
                        className="px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-medium text-xs flex items-center gap-1 transition-colors"
                      >
                        <span>{t('history.resubmit')}</span>
                        <ArrowUpRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>

                {/* Show Rejection Reason if present */}
                {c.status === 'rejected' && c.rejectionReason && (
                  <div className="mt-3 p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-xs text-red-700 dark:text-red-300 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                    <div>
                      <span className="font-semibold">{t('history.reviewerNote')}:</span> {c.rejectionReason}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
